import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import { createContext, useContext, useEffect, useState } from 'react'

interface SmoothScrollProps {
    children: ReactNode
}

const LenisContext = createContext<Lenis | null>(null)

export const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const [lenis, setLenis] = useState<Lenis | null>(null)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const instance = new Lenis({
            autoRaf: true,
            lerp: 0.085,
            wheelMultiplier: 0.95,
            smoothWheel: true
        })

        setLenis(instance)

        return () => {
            instance.destroy()
            setLenis(null)
        }
    }, [])

    return (
        <LenisContext.Provider value={lenis}>
            {children}
        </LenisContext.Provider>
    )
}

export const useLenis = (): Lenis | null => useContext(LenisContext)

/**
 * Freezes page scrolling while an overlay (menu, lightbox) is open.
 */
export const useScrollLock = (locked: boolean): void => {
    const lenis = useLenis()

    useEffect(() => {
        if (!locked) return

        const root = document.documentElement
        const previous = root.style.overflow

        lenis?.stop()
        root.style.overflow = 'hidden'

        return () => {
            root.style.overflow = previous
            lenis?.start()
        }
    }, [locked, lenis])
}
